import { CategoryLeaf, isCategoryFilter } from './filtersUtils';

/**
 * Service class for handling category-related operations
 * Centralizes all category logic to avoid duplication and improve maintainability
 */
export class CategoryService {
  /**
   * Extracts the display name for category filter values
   * @param {Object} item - The filter item
   * @param {string} name - The raw name value
   * @returns {string} - The display name (leaf for categories, original for others)
   */
  static getDisplayNameForFilter(item, name) {
    if (item != null && isCategoryFilter(item.mbName)) {
      return CategoryLeaf(name);
    }
    return name;
  }

  /**
   * Gets the value for category filter display
   * @param {Object} item - The filter item
   * @param {string} value - The value
   * @param {string} name - The name
   * @returns {string} - The appropriate display value
   */
  static getValueForFilter(item, value, name) {
    if (item != null && isCategoryFilter(item.mbName)) {
      return value;
    }
    return name;
  }

  /**
   * Parses category path and returns the leaf name
   * @param {string} categoryPath - The full category path (e.g., "Electronics>Computers>Laptops")
   * @returns {string} - The leaf category name
   */
  static getCategoryLeaf(categoryPath) {
    return CategoryLeaf(categoryPath);
  }

  /**
   * Checks if a filter is a category filter
   * @param {string} filterName - The filter name to check
   * @returns {boolean} - True if it's a category filter
   */
  static isCategoryFilter(filterName) {
    return isCategoryFilter(filterName);
  }

  /**
   * Gets the breadcrumb display for category selection
   * @param {string} categoryPath - The full category path
   * @returns {string} - The breadcrumb text
   */
  static getCategoryBreadcrumb(categoryPath) {
    return categoryPath;
  }

  /**
   * Formats selected category values for display
   * @param {Array} selectedValues - Array of selected category values
   * @param {Array} options - Available options with display_name
   * @returns {Array} - Array of display labels
   */
  static getSelectedCategoryLabels(selectedValues, options) {
    if (!selectedValues) return [];

    return selectedValues.map(value => {
      const option = options.find(opt => opt.value === value);
      return option ? option.display_name : CategoryLeaf(value);
    });
  }

  /**
   * Validates category path format
   * @param {string} categoryPath - The category path to validate
   * @returns {boolean} - True if valid
   */
  static isValidCategoryPath(categoryPath) {
    return typeof categoryPath === 'string' && categoryPath.length > 0;
  }
}